import React, { useEffect, useState, useMemo } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js/lib/core';
import bar from 'plotly.js/lib/bar';
import { CircularProgress, Box } from '@mui/material';

Plotly.register([bar]);
const Plot = createPlotlyComponent(Plotly);

const ProcessingStatusLineChart = ({ data, loading, error, onLoaded, hoveredPeriod, setLatestPeriod }) => {
  const [chartData, setChartData] = useState(null);
  const [latestPeriod, setInternalLatestPeriod] = useState(null);

  const activePeriod = useMemo(() => hoveredPeriod || latestPeriod, [hoveredPeriod, latestPeriod]);

  const formatPeriod = (period) => {
    if (!period) return 'Latest Period';
    const [year, month] = period.split('-');
    const date = new Date(Number(year), Number(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    const latest = data
      .filter((x) => x.paymentDatePeriodEpoch)
      .sort((a, b) => b.paymentDatePeriodEpoch - a.paymentDatePeriodEpoch)[0];

    if (latest?.paymentDatePeriod) {
      setInternalLatestPeriod(latest.paymentDatePeriod);
      setLatestPeriod?.(latest.paymentDatePeriod);
    }

    onLoaded?.();
  }, [data, setLatestPeriod, onLoaded]);

  useEffect(() => {
    if (!activePeriod || !data || data.length === 0) {
      setChartData(null);
      return;
    }

    const filtered = data.filter(
      (x) => x.paymentDatePeriod === activePeriod && x.lateOnTime?.toLowerCase() === 'late payment'
    );

    const xgroups = ['Invoices', 'Approvals', 'Payments'];

    const tracesOnTime = [
      filtered.filter((x) => x.invoiceProcessing === 'Invoice Processing On Time').length,
      filtered.filter((x) => x.approvals === 'Approval On Time').length,
      filtered.filter((x) => x.paymentProcessing === 'Payment processed on Time').length,
    ];

    const tracesLate = [
      filtered.filter((x) => x.invoiceProcessing === 'Late Invoice Processing').length,
      filtered.filter((x) => x.approvals === 'Late Approval').length,
      filtered.filter((x) => x.paymentProcessing === 'Payment Processing Delays').length,
    ];

    const onTimeTrace = {
      x: xgroups,
      y: tracesOnTime,
      type: 'bar',
      name: 'On Time',
      marker: { color: '#B0C3E5' },
      text: tracesOnTime.map(String),
      textposition: 'auto',
      showlegend: true,
      hovertemplate: 'OnTime: %{text}<extra></extra>',
    };

    const lateTrace = {
      x: xgroups,
      y: tracesLate,
      type: 'bar',
      name: 'Late',
      marker: { color: '#112856' },
      text: tracesLate.map(String),
      textposition: 'auto',
      showlegend: true,
      hovertemplate: 'Late: %{text}<extra></extra>',
    };

    setChartData([lateTrace, onTimeTrace]);
  }, [activePeriod, data]);

  const layout = {
    title: {
      text: `From Late Payments for: ${formatPeriod(activePeriod)}`,
      font: { size: 18 },
    },
    xaxis: { title: { text: 'Processing Stages' } },
    yaxis: { title: { text: 'Number of Payments' } },
    bargap: 0.3,
    barmode: 'stack',
    autosize: true,
    margin: { r: 100 },
    legend: { orientation: 'v', x: 1.05, y: 1, xanchor: 'center' },
    hovermode: 'x unified',
  };

  const config = {
    responsive: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['zoom2d', 'pan2d', 'select2d', 'lasso2d'],
    displayModeBar: 'hover',
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) return <p>Error: {error}</p>;
  if (!chartData) return <p>Hover over a bar to view details...</p>;

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <Plot data={chartData} layout={layout} config={config} style={{ width: '100%', height: '400px' }} />
    </div>
  );
};

export default ProcessingStatusLineChart;
